/**
 * Tracks whichever `document.startViewTransition()` call is currently in
 * flight, if any -- router-driven (`<Link viewTransition>`/`navigate({
 * viewTransition: true })`) or otherwise.
 *
 * Exists because Chromium aborts an active view transition the instant
 * another `history.pushState`/`replaceState` happens while it's still
 * animating (`.ready`/`.finished` reject with `InvalidStateError:
 * Transition was aborted because of invalid state`) -- `pages/issue/model/
 * issue.hooks.ts`'s `useArticle` redirect (`/issues/$id` ->
 * `/issues/$id/$articleId`, a `replace: true` navigation firing moments
 * after *every* issue open) is exactly that kind of history mutation, and
 * it was racing a `PreviewLink`'s cover-morph transition on the way in --
 * confirmed live: the transition's own `.ready` rejected with that exact
 * error on a real click-through. `getActiveViewTransition()` lets that
 * redirect wait for the transition to actually finish before mutating
 * history, instead of silently aborting it.
 *
 * Installed once, globally (`main.tsx`), rather than only within
 * `pages/issue`'s own bundle: the transition this needs to wait for is
 * started by whichever *other* page linked into the issue (home, a
 * collection, ...) before the issue page's own code has even loaded, so
 * the wrapper has to already be in place by the time that first
 * `document.startViewTransition` call happens.
 */
let active: ViewTransition | null = null;

export function installViewTransitionTracker(): void {
	if (typeof document === 'undefined') return;
	const native = document.startViewTransition?.bind(document);
	if (!native) return;

	document.startViewTransition = ((update: () => void | Promise<void>) => {
		const transition = native(update);
		active = transition;
		transition.finished
			.catch(() => {
				// A transition being skipped/interrupted is a normal outcome
				// (e.g. `prefersReducedMotion`, a second transition starting
				// before this one settles), not an error worth surfacing here --
				// callers of `getActiveViewTransition()` only care that it has
				// settled, not how.
			})
			.finally(() => {
				if (active === transition) {
					active = null;
				}
			});
		return transition;
	}) as typeof document.startViewTransition;
}

export function getActiveViewTransition(): ViewTransition | null {
	return active;
}
